import { Button, Spinner } from 'flowbite-react';
import React, { useState } from 'react';
import { HiOutlineStar } from 'react-icons/hi';
import { Drawer } from 'src/components/shared/Drawer';
import {
	ApplicationStatus,
	CampusUserWithApplication,
	DriveInviteeStatus,
	PositionStatus,
	UIPosition,
	UIUserRecommendation,
	UserExperienceLevelType,
} from 'types';
import PositionCreationModalFormForRecruiter from './PositionCreationModalFormForRecruiter';
import { useMediaQuery } from '@mui/material';
import RecruiterCandidateCardFooter from 'src/components/recruiterPositions/RecruiterCandidateCardFooter';
import dayjs from 'dayjs';
import { useGetRecommendationsForPositionQuery } from 'src/hooks/position.hooks';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PositionCandidatesTabsEnum } from '../freshersPosition/PositionCandidateListingTabs';
import { CandidatesTabsEnum } from 'src/components/recruiterPositions/RecruiterCandidatesContainer2';
import { useMutation, useQueryClient } from 'react-query';
import { shortlistUser } from 'src/apis/application';
import { useToast } from 'src/context/ToastProvider';
import classNames from 'classnames';
import { LightTooltip } from 'src/components/recruiterPositions/RecruiterCandidateCardHeader';
import { useLayoutContext } from 'src/context/LayoutProvider';

const FresherActionButton = ({
	studentData,
	selectedTab,
	isPositionCreated,
	showRoundProgress = false,
	showActionButton = true,
}: {
	isPositionCreated?: boolean;
	studentData: CampusUserWithApplication;
	selectedTab?: CandidatesTabsEnum | PositionCandidatesTabsEnum;
	showRoundProgress?: boolean;
	showActionButton?: boolean;
}) => {
	const toast = useToast();
	const navigate = useNavigate();
	const { positionId } = useParams();
	const queryClient = useQueryClient();
	const [searchParams] = useSearchParams();
	const isMobile = useMediaQuery('(max-width: 960px)');
	const { setShowLoadingAnimation } = useLayoutContext();
	const [createdPosition, setCreatedPosition] = useState<UIPosition>();
	const [openCreatePositionModal, setOpenCreatePositionModal] = useState(false);
	const {
		data: positionRecomendationData,
		isLoading: isPositionRecomendationDataLoading,
		isFetching: isFetchingRecommendations,
	} = useGetRecommendationsForPositionQuery({
		positionId: positionId ?? '',
		search: searchParams.get('search') || '',
	});

	const shortListUserFn = useMutation(['shortlistUser'], shortlistUser, {
		onSuccess() {
			toast({
				title: 'User Shortlisted successfully',
				type: 'success',
			});
		},
		onError(error: Error) {
			console.log('error:', error);
			setShowLoadingAnimation(false);
		},
	});

	const redirectAfterShortlist = () => {
		onShortlistDone && onShortlistDone();
		console.log('createdPosition:', createdPosition);
		navigate(`/view-candidates/position/${createdPosition?._id || ''}`);
		queryClient.refetchQueries(['getRecommendationsForPosition', createdPosition?._id]);
	};

	const positionHasExpired =
		positionRecomendationData?.position?.positionStatus === PositionStatus.CLOSED ||
		(positionRecomendationData?.position?.expiresAt && dayjs().isAfter(positionRecomendationData?.position?.expiresAt));

	const placedElseWhere = studentData?.application?.status === ApplicationStatus.PLACED_ELSEWHERE;
	const hasShownInterest =
		studentData?.application?.history?.map(h => h.newStatus).includes(ApplicationStatus.APPLIED_BY_JOBSEEKER) || false;
	const hasShownDisInterest =
		studentData?.application?.history?.map(h => h.newStatus).includes(ApplicationStatus.JOBSEEKER_OPTED_OUT) || false;

	if (isPositionRecomendationDataLoading) return <Spinner />;

	const recommendedUser = positionRecomendationData?.recommendedUsers?.find(
		e => e?.user?._id === studentData?._id,
	) as UIUserRecommendation;

	const connectedUser = positionRecomendationData?.connectedUsers?.find(
		e => e?.user?._id === studentData?._id,
	) as UIUserRecommendation;

	const interestedUser = positionRecomendationData?.interestedUsers?.find(
		e => e?.user?._id === studentData?._id,
	) as UIUserRecommendation;

	const inPipeLineUser = positionRecomendationData?.inPipeLineUsers?.find(
		e => e?.user?._id === studentData?._id,
	) as UIUserRecommendation;

	const combine = { ...recommendedUser, ...connectedUser, ...interestedUser };

	const combineWithPipeline = { ...recommendedUser, ...connectedUser, ...interestedUser, ...inPipeLineUser };

	const onShortlistDone = () => {
		queryClient.refetchQueries({
			queryKey: ['getCampusHiringCandidates'],
			exact: false, // This allows refetching of all matching queries
		});
		queryClient.refetchQueries({
			queryKey: ['getAllApplicantsForPosition'],
			exact: false, // This allows refetching of all matching queries
		});
	};

	const acceptedDrives = combineWithPipeline?.user?.invitedToDrives?.filter(
		e => e?.status === DriveInviteeStatus.ACCEPT,
	);

	return isPositionCreated ? (
		<div
			className={classNames(
				'flex gap-2 flex-wrap md:flex-col justify-between md:justify-center',
				showRoundProgress ? 'items-start' : 'items-center',
			)}
		>
			{selectedTab === PositionCandidatesTabsEnum.RECOMMENDATIONS ? (
				<div className="flex flex-wrap justify-center">
					{combineWithPipeline?.user?.invitedToDrives?.length ? (
						<div>
							<div className="flex flex-col items-center gap-2 justify-center font-inter text-center md:mb-1 mr-2 md:mr-0">
								{acceptedDrives?.length ? (
									<LightTooltip
										light={true}
										title={
											<div className="p-2 text-xs text-center text-[#555770] font-[500]">
												{acceptedDrives?.map(e => (
													<div className="" key={e?._id}>
														{e?.drive?.city},
														{e?.drive?.driveEndDate && e?.drive?.driveEndDate !== "" && e?.drive?.driveEndDate !== null ? `${dayjs(e?.drive?.driveDate).format('Do MMM')} - ${dayjs(e?.drive?.driveEndDate).format('Do MMM')}` : `${dayjs(e?.drive?.driveDate).format('Do MMM')}`}
													</div>
												))}
											</div>
										}
										placement="bottom"
										arrow={true}
										componentsProps={{
											tooltip: {
												sx: {
													border: '0px !important',
													borderRadius: '4px !important',
													boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
												},
											},
										}}
									>
										<div className="text-[#555770] text-xs font-inter font-[500] border-b border-dashed border-[#555770]">
											{acceptedDrives?.length} Accepted {acceptedDrives?.length > 1 ? 'Drives' : 'Drive'}
										</div>
									</LightTooltip>
								) : null}

								{combineWithPipeline?.user?.invitedToDrives?.length ? (
									<LightTooltip
										light={true}
										title={
											<div className="p-2 text-xs text-center text-[#555770] font-[500]">
												{combineWithPipeline?.user?.invitedToDrives?.map(e => (
													<div className="" key={e?._id}>
														{e?.drive?.city}, {e?.drive?.driveEndDate ? `${dayjs(e?.drive?.driveDate).format('Do MMM')} - ${dayjs(e?.drive?.driveEndDate).format('Do MMM')}` : `${dayjs(e?.drive?.driveDate).format('Do MMM')}`}{' '}
													</div>
												))}
											</div>
										}
										placement="bottom"
										arrow={true}
										componentsProps={{
											tooltip: {
												sx: {
													border: '0px !important',
													borderRadius: '4px !important',
													boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
												},
											},
										}}
									>
										<div className="!rounded-[6px] bg-[#EDEBFE] px-[10px] text-[#5521B5] text-xs text-[500] py-1">
											Invited for {combineWithPipeline?.user?.invitedToDrives?.length > 1 ? 'Drives' : 'Drive'}
										</div>
									</LightTooltip>
								) : null}
							</div>
						</div>
					) : null}

					{combine?.application?.status === ApplicationStatus.OPENED_BY_ADMIN ||
						combine?.application?.status === ApplicationStatus.OPENED_BY_EMPLOYER ? (
						<div className="w-fit bg-[#EDEBFE] px-[10px] py-[4px] rounded-[6px] flex items-center mx-auto">
							<p className=" text-[#5521B5] font-inter text-[14px] whitespace-nowrap font-[500] leading-[18px]">
								Invite Sent
							</p>
						</div>
					) : null}
					{!placedElseWhere && !hasShownInterest && hasShownDisInterest ? (
						<div className="w-fit bg-[#FFF8E5] px-[10px] py-[4px] rounded-[6px] flex items-center">
							<p className=" text-[#FF8800] font-inter text-[14px] whitespace-nowrap font-[500] leading-[18px]">
								Not Interested
							</p>
						</div>
					) : null}
					{!connectedUser && !placedElseWhere && hasShownInterest ? (
						<div className="w-fit bg-[#FCE8F3] px-[10px] py-[4px] rounded-[6px] flex items-center">
							<p className=" text-[#99154B] font-inter text-[14px] font-[500] leading-[18px]">Interested</p>
						</div>
					) : null}
					{placedElseWhere ? (
						<div className="w-fit bg-[#FCE8F3] px-[10px] py-[4px] rounded-[6px] flex items-center">
							<p className=" text-[#99154B] font-inter text-[14px] font-[500] leading-[18px]">Placed Elsewhere</p>
						</div>
					) : null}
				</div>
			) : null}

			{connectedUser && selectedTab === PositionCandidatesTabsEnum.SHORTLISTED ? (
				<div className="w-fit bg-[#DEF7EC] px-[10px] py-[4px] rounded-[6px] flex items-center">
					<p className=" text-[#03543F] font-inter text-[14px] font-[500] leading-[18px]">Opted-In</p>
				</div>
			) : null}

			<RecruiterCandidateCardFooter
				showRoundProgress={showRoundProgress}
				showActionButton={
					selectedTab === PositionCandidatesTabsEnum.CONNECTED ||
						(!inPipeLineUser &&
							combine?.application?.history?.map(h => h.newStatus).includes(ApplicationStatus.SHORTLISTED))
						? showActionButton
						: false
				}
				userId={studentData?._id}
				data={selectedTab === PositionCandidatesTabsEnum.CONNECTED ? inPipeLineUser : combine}
				isShortListed={
					combine?.application?.history?.map(h => h.newStatus).includes(ApplicationStatus.OPENED_BY_EMPLOYER) ||
						combine?.application?.history?.map(h => h.newStatus).includes(ApplicationStatus.SHORTLISTED)
						? true
						: false
				}
				showShortListUi={selectedTab === PositionCandidatesTabsEnum.RECOMMENDATIONS}
				isPositionExpired={positionHasExpired}
				level={Math.round(studentData.evaluation.level ?? 0)}
				disableStartProcess={
					positionRecomendationData?.position?.positionStatus === PositionStatus.CLOSED ||
					combine?.application?.pendingForAdminApproval ||
					combine?.application?.status === ApplicationStatus.REJECTED
				}
				disableViewProfile={positionRecomendationData?.position?.positionStatus === PositionStatus.CLOSED}
				hasShownDisInterest={false}
				hasShownInterest={
					combine?.application?.history?.map(h => h.newStatus).includes(ApplicationStatus.APPLIED_BY_JOBSEEKER)
						? true
						: false
				}
				handleViewProfile={() => { }}
				handler={studentData.handler}
				containerProps={{
					className:
						inPipeLineUser && selectedTab === PositionCandidatesTabsEnum.CONNECTED ? 'w-full' : 'w-fit ml-auto md:ml-0',
				}}
				onShortlistDone={onShortlistDone}
			/>
		</div>
	) : (
		<div className="flex justify-end md:justify-center">
			<Button
				color="light"
				// disabled={shortListUserFn?.isLoading}
				// isProcessing={shortListUserFn?.isLoading}
				onClick={() => {
					setOpenCreatePositionModal(true);
				}}
				className="[&_span]:!p-0 !px-[16px] !py-[8px] bg-[#FAFAFC] border-[#7777E7] text-[#7777E7]"
			>
				<HiOutlineStar className="mr-[8px]" />
				Shortlist
			</Button>

			<Drawer
				fullWidth={true}
				backgroundColor="#F7F9FB"
				backDropColor="rgba(0, 0, 0, 0.70)"
				header={
					<div className="px-0 md:px-[14px] pt-6">
						<div className="text-[#004FC4] mx-[16px] font-[400] text-sm md:text-base py-2 md:py-[15px] px-[12px] md:px-[19px] rounded-[8px] bg-[#E5F0FF]">
							Kindly confirm the job details before you proceed with shortlisting candidates.
						</div>
					</div>
				}
				isModal={!isMobile}
				modalMaxWidth="760px"
				isOpen={openCreatePositionModal}
				body={
					<div>
						<PositionCreationModalFormForRecruiter
							isModal={true}
							loading={shortListUserFn?.isLoading}
							onFinish={({ position }) => {
								shortListUserFn.mutate(
									{
										positionId: position?._id || '',
										userId: studentData?._id,
									},
									{
										onSuccess(data) {
											setShowLoadingAnimation(false);
											redirectAfterShortlist();
											onShortlistDone();
											navigate(`/view-candidates/position/${position?._id}`);
										},
									},
								);
							}}
							experienceLevelBuckets={
								window.location?.pathname?.includes('/experience-candidates')
									? [UserExperienceLevelType.OneToTwoYears, UserExperienceLevelType.TwoPlusYears]
									: [UserExperienceLevelType.FRESHER]
							}
						/>
					</div>
				}
				onClose={() => {
					setOpenCreatePositionModal(false);
				}}
			/>
		</div>
	);
};

export default FresherActionButton;
